// src/pages/LoginPage/LoginPage.jsx
import React, { useState, useEffect, useContext } from "react";
import "./LoginPage.css";
import { TbLogin2 } from "react-icons/tb";
import LoginLogo from "../../assets/images/login-logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Services from "../../services/Services";
import GooglePlayLogo from "../../assets/googleplay.png";
import AppStoreLogo from "../../assets/AppStoreLogo.png";
import { GeneralContext } from "../../GeneralProvider";
import { useToaster } from "../../ToasterProvider";
import { useLanguage } from "../../LanguageProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { broadcastAuthMessage, subscribeAuthMessage, isRedirectSuppressed } from "../../utils/authChannel";
import SHEventDetail from "../SocialHive/views/SHEventInfoPage/views/SHEventDetail";
import MobileLoginEvent from "./MobileLoginEvent";

export default function LoginPage() {
  const { toastError, toastWarning } = useToaster();
  const { getTranslation } = useLanguage();
  const navigate = useNavigate();
  const { roomId, channelUri, eventID } = useParams();
  const {
    handleGoogleLogin,
    handleGithubLogin,
    handleTokenUpdate,
    handleSelectProject,
    getUserAuthorizations,
    setIsSubscriptionExpired,
    setSubscriptionRenewDate,
    setSubscriptionType,
    setShChannelUri,
    setDmRoomUri,
    setEventIDUri,
    setEventUriTab,
  } = useContext(GeneralContext);
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 👇 EVENT PREVIEW STATE
  const [eventPreview, setEventPreview] = useState(null);
  const [soldTicketsCount, setSoldTicketsCount] = useState(0);

  const [mode, setMode] = useState('details');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const userToken =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const [hasSeenPrompt] = useState(
    localStorage.getItem("mobilePromptSeen") === "true"
  );

  useEffect(() => {
    const unsubscribe = subscribeAuthMessage((data) => {
      if (!data || typeof data !== "object") return;

      // 🔒 Global guard: MobileLoginEvent gibi yerlerden suppress geldiyse yönlendirme yapma
      if (data?.suppressRedirect || isRedirectSuppressed()) {
        // Yine de storage ve context güncellensin ki app state bozulmasın
        if (data.type === "LOGIN" && data.token && data.user) {
          const remember = data.rememberMe === true || localStorage.getItem("rememberMe") === "true";
          if (remember) {
            localStorage.setItem("token", data.token);
            localStorage.setItem("user", JSON.stringify(data.user));
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.setItem("rememberMe", "false");
          }
          try { handleTokenUpdate?.(data.token); getUserAuthorizations?.(data.token, data.user.id); } catch { }
        }
        return; // ❌ yönlendirme yok
      }

      if (data.type === "I_HAVE_SESSION" && data.token && data.user) {
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("user", JSON.stringify(data.user));
        try { handleTokenUpdate?.(data.token); getUserAuthorizations?.(data.token, data.user.id); } catch { }
        if (!(isMobile && eventID)) {
          navigate("/home");
        }
        return;
      }

      if (data.type === "LOGIN" && data.token && data.user) {
        if (data.rememberMe === true || localStorage.getItem("rememberMe") === "true") {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.setItem("rememberMe", "false");
        }

        try {
          handleTokenUpdate?.(data.token);
          getUserAuthorizations?.(data.token, data.user.id);
        } catch (e) { }
        if (!(isMobile && eventID)) {
          navigate("/home");
        }
      }

      if (data.type === "LOGOUT") {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("rememberMe");
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [navigate, handleTokenUpdate, getUserAuthorizations]);

  useEffect(() => {
    broadcastAuthMessage({ type: "WHO_HAS_SESSION" });
  }, []);

  useEffect(() => {
    if (channelUri) {
      setShChannelUri(channelUri);
    } else if (roomId) {
      setDmRoomUri(roomId);
    } else if (eventID) {
      setEventIDUri(eventID);
      const pathSegments = location.pathname.split("/");
      const eventDetailsTab = pathSegments[3];
      setEventUriTab(eventDetailsTab);
    }
  }, [channelUri, roomId, eventID, location.pathname, setDmRoomUri, setEventIDUri, setEventUriTab, setShChannelUri]);

  const handleLogin = () => {
    setIsLoading(true);
    Services.UserLogin(token, username, password)
      .then((res) => {
        if (res.data.status) {
          getUserAuthorizations(res.data.result.token, res.data.result.id);
          setIsSubscriptionExpired(res.data.result.isExpired);
          setSubscriptionRenewDate(res.data.result.subscriptionExpiredDate);
          setSubscriptionType(res.data.result.subscriptionPackageType);
          if (rememberMe) {
            localStorage.setItem("username", username);
            localStorage.setItem("user", JSON.stringify(res.data.result));
            localStorage.setItem("token", res.data.result.token);
            localStorage.setItem("rememberMe", true);

            window.dispatchEvent(new Event("app-token-changed"));

            // (İsteğe bağlı) Sekmeler arası senkron da istiyorsan:
            try {
              const bc = new BroadcastChannel('auth');
              bc.postMessage({
                type: 'token',
                value: res.data.result.token
              });
              bc.close();
            } catch (_) { }
          } else {
            localStorage.setItem("temp_username", username);
            sessionStorage.setItem("user", JSON.stringify(res.data.result));
            sessionStorage.setItem("token", res.data.result.token);
            localStorage.setItem("rememberMe", false);
            window.dispatchEvent(new Event("app-token-changed"));
            try {
              const bc = new BroadcastChannel('auth');
              bc.postMessage({
                type: 'token',
                value: res.data.result.token
              });
              bc.close();
            } catch (_) { }
          }
          handleTokenUpdate(res.data.result.token);
          if (res.data.result.selectedProject) {
            handleSelectProject(
              res.data.result.selectedProject,
              res.data.result.token,
              false,
              "",
              res.data.result.id
            );
          }

          broadcastAuthMessage({
            type: "LOGIN",
            token: res.data.result.token,
            user: res.data.result,
            rememberMe
          });
          if (!(isMobile && eventID)) {
            navigate("/home");
          }
        } else {
          toastError(getTranslation("invalidCredentials"));
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  const getToken = () => {
    Services.GetToken()
      .then((res) => {
        if (res.data.status) {
          setToken(res.data.result.token);
          Services.CreatePageView(res.data.result.token, "login").catch(() => { });
          if (userToken && (localStorage.getItem("rememberMe") === "true")) {
            handleAutoLogin();
          }
        }
      })
      .catch(() => { });
  };

  const handleAutoLogin = () => {
    const safeParse = (s) => { try { return s ? JSON.parse(s) : null } catch { return null } };
    const uLocal = safeParse(localStorage.getItem("user"));
    const uSession = safeParse(sessionStorage.getItem("user"));
    const userID = uLocal?.id ?? uSession?.id ?? null;

    if (userToken && (localStorage.getItem("rememberMe") === "true")) {
      getUserAuthorizations(userToken, userID);
      if (!(isMobile && eventID)) {
        navigate("/home");
      }
    } else {
      toastError(getTranslation("invalidCredentials"));
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  useEffect(() => {
    const handleAutoLogin = () => {
      const safeParse = (s) => { try { return s ? JSON.parse(s) : null } catch { return null } };
      const uLocal = safeParse(localStorage.getItem("user"));
      const uSession = safeParse(sessionStorage.getItem("user"));
      const userID = uLocal?.id ?? uSession?.id ?? null;

      if (userToken && (localStorage.getItem("rememberMe") === "true")) {
        getUserAuthorizations(userToken, userID);
        if (!(isMobile && eventID)) {
          navigate("/home");
        }
      } else {
        toastError(getTranslation("invalidCredentials"));
      }
    };

    const getToken = () => {
      Services.GetToken()
        .then((res) => {
          if (res.data.status) {
            setToken(res.data.result.token);
            Services.CreatePageView(res.data.result.token, "login").catch(() => { });
            if (userToken && (localStorage.getItem("rememberMe") === "true")) {
              handleAutoLogin();
            }
          }
        })
        .catch(() => { });
    };

    getToken();
  }, [userToken, getUserAuthorizations, navigate, toastError, getTranslation]);

  // 👇 Fetch readonly event details for left-side preview (no comments)
  useEffect(() => {
    if (!eventID || !token) return;

    const getSoldTicketsCount = () => {
      Services.SocialHiveGetEventSoldTickets(token, eventID)
        .then((res) => {
          if (res?.data?.status) {
            setSoldTicketsCount(res.data.result?.length || 0);
          } else {
            setSoldTicketsCount(0);
          }
        })
        .catch(() => {
          setSoldTicketsCount(0);
        });
    };

    Services.SocialHiveGetEventDetails(token, eventID)
      .then((res) => {
        if (res?.data?.status) {
          setEventPreview(res.data.result || null);
          // Fetch sold tickets count after event details are loaded
          getSoldTicketsCount();
        } else {
          setEventPreview(null);
        }
      })
      .catch(() => {
        setEventPreview(null);
      });
  }, [eventID, token, mode]);

  if (isMobile && !hasSeenPrompt && !eventID) {
    return (
      <div className="mobile-prompt">
        <h2>Welcome to the Lodos.io desktop panel</h2>
        <p>For a better experience on your phone, download our mobile app:</p>
        <div className="mobile-links">
          <span>Android App:</span>
          <a href="https://play.google.com/store/apps/details?id=io.lodos.app&pli=1">
            <img style={{ width: "90px", height: "90px" }} src={GooglePlayLogo} alt="Google Play Store" />
          </a>
        </div>
        <div className="mobile-links">
          <span>iOS App</span>
          <a href="https://apps.apple.com/tr/app/lodos-io/id6745250622">
            <img style={{ width: "90px", height: "90px" }} src={AppStoreLogo} alt="App Store" />
          </a>
        </div>
        <span>Or You Can Sign Up Now!</span>
        <button
          onClick={() =>
            eventID
              ? navigate(`/register${location.pathname}`)
              : navigate("/register")
          }
          className="redirect-button"
          style={{ position: 'relative', right: 0, alignSelf: 'center' }}
        >
          Sign Up <TbLogin2 size={25} />
        </button>
      </div>
    );
  }

  const rememberMeFlag = localStorage.getItem("rememberMe") === "true";
  const hasPersistentPersistentLogin = rememberMeFlag && !!localStorage.getItem("token");
  const hasSessionLogin = !!sessionStorage.getItem("token");

  if (hasPersistentPersistentLogin || hasSessionLogin) {
    // Mobil etkinlik preview'ındaysak ya da global suppress aktifse yönlendirme yapma
    if (isMobile && eventID) {
      // stay on MobileLoginEvent
    } else if (isRedirectSuppressed()) {
      // short-lived suppress sırasında kal
    } else {
      navigate("/home");
      return null;
    }
  }

  if (isMobile && eventID && eventPreview) {
    return (
      <MobileLoginEvent event={eventPreview} mode={mode} setMode={setMode} />
    )
  }

  else if (!eventID || !isMobile) {
    return (
      <div className={`container ${eventID ? 'event-preview' : ''}`}>
        <div className="left-circle"></div>
        <div className="right-circle"></div>

        <button
          onClick={() =>
            eventID
              ? navigate(`/register${location.pathname}`)
              : navigate("/register")
          }
          className="redirect-button"
        >
          Sign Up <TbLogin2 size={25} />
        </button>

        <div className="left-section">
          {eventID ?
            <div className="left-section-inner-container-preview">
              <SHEventDetail
                event={eventPreview}
                isAdmin="0"
                getEventDetails={() => { }}
                activeTab="details"
                isPreview
                channelLogo={eventPreview?.channelDetails?.logo}
                channelTitle={eventPreview?.channelDetails?.title}

              />
            </div>
            :
            <div className="left-section-inner-container">
              <span className="left-section-text">
                Manage Your Workforce in{" "}
                <span className="left-section-inner-text">One Space</span>
              </span>
              <span className="left-section-subtitle">
                Sign in now and start the journey
              </span>
            </div>
          }

        </div>

        <div className="right-section">
          <div className="right-section-inner-container">
            <img src={LoginLogo}  height={50} alt="Lodos" />
            {/* <span className="right-section-text">
              Welcome to <span className="right-section-inner-text">Lodos</span>
            </span> */}
            <span className="right-section-subtitle">
              Let's manage your workforce together!
            </span>

            <div className="auth-form">
              <form
                style={{ width: "100%" }}
                onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
              >
                <label className="input-label">Email*</label>
                <input
                  className="input-default"
                  type="text"
                  id="username"
                  placeholder="Username / Email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <label className="input-label">Password*</label>
                <div className="input-container">
                  <input
                    className="input-default"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                        toastWarning(getTranslation("passwordCannotContainSpaces"));
                      }
                    }}
                  />
                  <span className="password-toggle" onClick={togglePasswordVisibility}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <div className="remember-me">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  <label htmlFor="rememberMe">Remember me</label>
                </div>
                <button type="submit" className="login-button">
                  {isLoading ? <div className="spinner"></div> : "Get Started"}
                </button>
              </form>
            </div>

            <span className="terms-text">
              By creating an account, you agree to our Terms of Service and
              Privacy & Cookie Statement.
            </span>

            <span className="already-text">
              Don't have an account?{" "}
              <span onClick={() =>
                eventID
                  ? navigate(`/register${location.pathname}`)
                  : navigate("/register")
              } className="second-sign-up">
                Sign up
              </span>
            </span>

            <span className="already-text">
              <span onClick={() => navigate("/forgot-password")} className="second-sign-up">
                {getTranslation("forgotPassword")}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

}